<template>
  <div>
    <div v-if="isCurrentRoute && !loading">
      <v-card elevation="0" color="transparent">
        <v-card-text>
          <v-form
            @submit.prevent="onSubmit"
            ref="form"
            v-if="attributeListFiltered.length"
            v-model="valid"
          >
            <template v-for="attribute in attributeListFiltered">
              <div
                :key="attribute.name"
                v-if="
                  checkPrerequisiteList(attribute) &&
                  !!attribute.component &&
                  attribute.component !== 'DefaultValue'
                "
              >
                <label class="och-label"
                  >{{ attribute.label }}
                  <span v-if="valueIsRequired(attribute)"> *</span>
                  <span
                    v-if="
                      attribute[filterAttribute] &&
                      filterAttribute === 'excludeUpdate'
                    "
                    >(Cannot edit after {{ entityName }} creation)</span
                  ></label
                >
                <div :data-cy="`${attribute.name}Input`">
                  <component
                    :attributeName="attribute.name"
                    :componentConfig="attribute.componentConfig"
                    :configMap="attributeMap"
                    :disabled="
                      !checkPrerequisiteList(attribute) ||
                      attribute[filterAttribute] ||
                      (locked && attribute.lockable)
                    "
                    :counter="attribute.maxLength"
                    :entityName="attribute.entityName"
                    :helpText="attribute.helpText"
                    :hint="attribute.hint"
                    :is="attribute.component"
                    :mode="mode"
                    :optionAttributeEntityName="
                      attribute.optionAttributeEntityName
                    "
                    :optionConfig="attribute.option"
                    :parentEntityName="attribute.parentEntityName"
                    :ruleList="getRuleList(attribute)"
                    v-model="attributeMap"
                  ></component>
                </div>
              </div>
            </template>
          </v-form>
        </v-card-text>

        <v-card-actions class="mt-8">
          <v-btn
            color="primary"
            data-cy="formCancel"
            outlined
            @click="backToParent()"
            >Cancel</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn
            :disabled="!valid"
            @click="save"
            color="primary"
            data-cy="formSubmit"
            outlined
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>

      <div v-if="Array.isArray(associationList) && associationList.length">
        <h2>Associations</h2>
        <div v-for="(association, index) in associationList" :key="index">
          <v-btn
            class="mr-2"
            color="primary"
            outlined
            large
            :to="Object.assign({}, { params: routeParamMap }, association.to)"
            >{{ association.label }}</v-btn
          >
        </div>
      </div>
    </div>
    <div v-else-if="loading">
      <generic-loader></generic-loader>
    </div>

    <router-view></router-view>
  </div>
</template>

<script>
import { clone, get, has, isEqual, pick, upperFirst } from "lodash";
import { mapActions, mapGetters } from "vuex";
import axios from "axios";

import * as formElementMap from "./formElement";
import GenericLoader from "@/components/GenericLoader";

const operatorMap = {
  isEqual,
  isNotEqual: (actualValue, value) => !isEqual(actualValue, value),
};

export default {
  components: {
    ...formElementMap,
    GenericLoader,
  },
  computed: {
    locked() {
      if (!this.attributeMap) {
        return false;
      }
      return this.attributeMap.locked;
    },
    attributeList() {
      let attributeList = this.attributeFilter({
        entityName: this.entityName,
      });

      attributeList.sort(({ orderNumber: first }, { orderNumber: second }) => {
        if (first === undefined && second === undefined) {
          return 0;
        }

        if (first === undefined) {
          return 1;
        }

        if (second === undefined) {
          return -1;
        }

        if (first === second) {
          return 0;
        }

        return first < second ? -1 : 1;
      });

      return attributeList;
    },
    attributeListFiltered() {
      return this.attributeList.filter(
        attribute => attribute.name !== "locked"
      );
    },
    entityHasCareSettingAttributes() {
      return (
        has(this.attributeMap, "ambulatory") && has(this.attributeMap, "acute")
      );
    },
    excludeAttribute() {
      const { mode } = this;

      const excludeAttribute = `exclude${upperFirst(mode)}`;

      return excludeAttribute;
    },
    filterAttribute() {
      const { mode } = this;

      const filterAttribute = `exclude${upperFirst(mode)}`;

      return filterAttribute;
    },
    isCurrentRoute() {
      const {
        $route: { name: currentRouteName },
        routeName,
      } = this;

      return currentRouteName === routeName;
    },
    requiredAttributeName() {
      const { mode } = this;

      const requiredAttributeName = `required${upperFirst(mode)}`;

      return requiredAttributeName;
    },
    routeParamMap() {
      return this.$route.params;
    },
    ...mapGetters({
      attributeFilter: "attribute/filter",
      getFileById: "file2/getById",
    }),
  },
  async created() {
    this.loading = true;
    let attributeMap = Object.assign({}, this.additionalAttributeMap);

    if (this.mode === "create") {
      this.attributeMap = attributeMap;
      this.loading = false;
      return;
    }

    try {
      await this.entityGetList({ reload: false });
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: "Error loading data from server" });
      return;
    }
    const entity = this.$store.getters[`${this.entityName}/getById`](this.id);

    attributeMap = clone(entity);

    delete attributeMap[this.primaryAttributeName];

    this.attributeMap = attributeMap;
    this.loading = false;
  },
  data() {
    return {
      attributeMap: {},
      fileUpload: undefined,
      valid: false,
      loading: false,
    };
  },
  methods: {
    valueIsRequired(attribute) {
      const { requiredAttributeName } = this;
      const { allowNull, name, prerequisiteListRequired } = attribute;

      const requiredByDefinition = attribute[requiredAttributeName];
      const requiredByPrereqList = this.checkPrerequisiteList({
        name,
        prerequisiteList: prerequisiteListRequired,
        defaultReturn: false,
      });
      const nullNotAllowed = allowNull === false;

      return requiredByDefinition || requiredByPrereqList || nullNotAllowed;
    },
    careSettingIsValid() {
      // If both are set to false, the form is invalid.
      return !(
        this.attributeMap.ambulatory === false &&
        this.attributeMap.acute === false
      );
    },

    backToParent() {
      const {
        $route: { matched },
      } = this;

      const parent = matched[matched.length - 2];
      this.$router.push(parent.path);
    },
    checkPrerequisiteList({ name, prerequisiteList, defaultReturn }) {
      if (defaultReturn === undefined) {
        defaultReturn = true;
      }
      if (!prerequisiteList) {
        return defaultReturn;
      }

      return prerequisiteList.reduce(
        (acc, { attributeName, operator, value }) => {
          let actualValue = this.attributeMap[attributeName];
          if (actualValue === undefined || actualValue === null) {
            actualValue = false;
          }

          const operatorFn = operatorMap[operator];

          if (!operatorFn) {
            throw new Error(
              `Prerequiste operator ${operator} not found for ${name}`
            );
          }

          if (!defaultReturn) {
            return acc || operatorFn(actualValue, value);
          }

          return acc && operatorFn(actualValue, value);
        },
        defaultReturn
      );
    },
    async entityCreate(attributeMap) {
      return this.$store.dispatch(`${this.entityName}/create`, attributeMap);
    },
    async entityGetList() {
      return this.$store.dispatch(`${this.entityName}/list`);
    },
    async entityUpdate([id, attributeMap]) {
      return this.$store.dispatch(`${this.entityName}/update`, [
        id,
        attributeMap,
      ]);
    },
    getRuleList(attribute) {
      const { valueIsRequired } = this;
      const result = [];

      const requiredRule = v =>
        (v !== undefined && v !== null) ||
        (Array.isArray(v) && v.length > 0) ||
        `${attribute.label} is required`;

      if (valueIsRequired(attribute)) {
        result.push(requiredRule);
      }

      if (
        (this.entityHasCareSettingAttributes &&
          attribute.name === "ambulatory") ||
        attribute.name === "acute"
      ) {
        const careSettingRequiredRule = () =>
          this.careSettingIsValid() || `Select Acute or Ambulatory or both`;

        result.push(careSettingRequiredRule);
      }

      return result;
    },
    async save() {
      const {
        attributeList,
        attributeMap: attributeMapBase,
        entityName,
        excludeAttribute,
        id,
        mode,
      } = this;

      const allowedAttrNameList = attributeList
        .filter(attribute => {
          // 1st, check & exclude meta-attributes if they're excluded.
          // 2nd, if they're not excluded, INCLUDE them if the main attribute entity ISN'T locked.
          // 3rd, if they're not excluded AND the parent entity is locked, include the meta-attribute if it ISN'T "lockable".
          return (
            !attribute[excludeAttribute] &&
            (!attributeMapBase.locked || !attribute.lockable)
          );
        })
        .map(({ name }) => name);

      const attributeMap = pick(attributeMapBase, allowedAttrNameList);

      try {
        if (mode === "create") {
          await this.entityCreate(attributeMap);
        } else {
          await this.entityUpdate([id, attributeMap]);
        }
      } catch (e) {
        console.error(e);

        const data = get(e, "response.data");
        if (data) {
          const { errors, message } = data;

          if (!errors) {
            this.$notifyError({ message });
            return;
          }

          const errorMessageList = errors.map(({ message }) => message);

          this.$notifyError({
            message: errorMessageList
              .map(message => `- ${upperFirst(message)}`)
              .join("<br>"),
          });
          return;
        }

        this.$notifyError({ message: `Could not ${mode} ${entityName}.` });
        return;
      }

      this.$refs.form.resetValidation();
      await this.entityGetList();
      this.backToParent();
    },
    upperFirst,
    async upload(attributeName) {
      const formData = new FormData();

      formData.append("file", this.fileUpload);

      let response;
      try {
        response = await axios({
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          method: "post",
          url: "/api/file2",
        });
      } catch (e) {
        let message = "Error uploading file";
        if (e.response) {
          message = e.response.data;
        }

        this.$notifyError({ message });
        return;
      }

      const { result: fileId } = response.data;

      await this.fileGetList();
      this.$set(this.attributeMap, attributeName, fileId);
    },
    async uploadClear(attributeName) {
      const fileId = this.attributeMap[attributeName];

      try {
        await axios({
          method: "delete",
          url: `/api/file2/${fileId}`,
        });
      } catch (e) {
        let message = "Error removing file";
        if (e.response) {
          message = e.response.data;
        }

        this.$notifyError({ message });
        return;
      }

      await this.fileGetList();
      this.$set(this.attributeMap, attributeName, null);
    },
    ...mapActions({
      fileGetList: "file2/list",
    }),
  },
  props: {
    associationList: {
      default: () => [],
      type: Array,
    },
    entityName: {
      required: true,
      type: String,
    },
    id: {},
    mode: {
      default: "create",
      type: String,
      validator: function (value) {
        return ["create", "update"].includes(value);
      },
    },
    routeName: {
      type: String,
      required: true,
    },
  },
  updated() {
    const {
      $refs: { form },
    } = this;

    // Depending on v-if, form may not be defined
    if (!form) {
      return;
    }

    // Show validation error in case bad data was previously saved
    form.validate();
  },
};
</script>
